import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Modal from '@shared/ui/Modal';
import {
  Vote,
  Users,
  BarChart3,
  Loader2,
  CheckCircle,
  XCircle,
  Minus,
  UserX,
  User,
  Download,
  ExternalLink,
  StickyNote,
  Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { WorkspacePermissions } from '../../../types/workspace';
import api from '../../../lib/api';
import Button from '../../Button';
import SearchField from '../../SearchField';
import BulkVotePredictionModal from '../../BulkVotePredictionModal';
import { formatPartyDisplay, getPartyBadgeColor, matchesPartyFilter } from '../../../utils/partyUtils';
import { getLegislatorLinkClasses } from '../../../utils/linkStyles';
import { useEntityNotes } from '../../../hooks/useNotesV2';
import ViewNotesModal from '../../notes/ViewNotesModal';
import { HelpTrigger } from '../../../help';
import { formatLegislatorNameFromObject } from '../../../utils/nameUtils';
import DistrictMapLink from '../../DistrictMapLink';
import { prefetchHostedDistrictMapUrls } from '../../../utils/districtMapUrls';
import FilterActions from '../../FilterActions';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../../StandardTable';
import StatCard, { StatCardRow } from '../../StatCard';
import { formatDistrict } from '../../../utils/districtDisplay';

// Notes cell component for vote predictions - ties notes to specific legislator + bill combination
interface NotesCellProps {
  legislatorId: string;
  legislatorName: string;
  billId: string;
  billNumber: string;
  workspaceId: string;
  noteCount: number;  // Pre-loaded from API
}

const NotesCell: React.FC<NotesCellProps> = ({ legislatorId, legislatorName, billId, billNumber, workspaceId, noteCount }) => {
  const [showModal, setShowModal] = useState(false);

  // Use vote_prediction entity type with composite ID for legislator+bill specific notes
  // Only fetch full notes when modal is opened to avoid N+1 API calls
  const votePredictionEntityId = `${legislatorId}:${billId}`;
  const { notes, isLoading } = useEntityNotes(
    'vote_prediction',
    votePredictionEntityId,
    workspaceId,
    100,
    showModal  // Only fetch when modal is open
  );

  const displayName = `${legislatorName} on ${billNumber}`;

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowModal(true);
        }}
        className={`inline-flex items-center px-2 py-1 text-xs rounded-full cursor-pointer transition-colors ${
          noteCount > 0
            ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200 hover:bg-primary-200 dark:hover:bg-primary-900/40'
            : 'bg-white dark:bg-background-dark-secondary text-text-tertiary dark:text-text-dark-tertiary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
        }`}
        title={noteCount > 0 ? `View ${noteCount} note${noteCount !== 1 ? 's' : ''} for ${displayName}` : `Add note for ${displayName}`}
      >
        <StickyNote className="w-3 h-3 mr-1" />
        <span className="font-medium">{noteCount}</span>
      </button>

      {showModal && (
        <ViewNotesModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          notes={isLoading ? [] : notes.map(note => ({
            id: note.id,
            title: note.title,
            content: note.content,
            created_at: note.created_at,
            updated_at: note.updated_at,
            include_in_reports: note.include_in_reports,
            author_name: note.author_name || 'Unknown',
            tags: note.tags || [],
            parent_note_id: note.parent_note_id,
            reply_count: note.reply_count || 0
          }))}
          entityType="vote_prediction"
          entityId={votePredictionEntityId}
          entityDisplayName={displayName}
          workspaceId={workspaceId}
        />
      )}
    </>
  );
};

interface Legislator {
  id: number;
  full_name: string;
  first_name: string;
  last_name: string;
  party_affiliation: string;
  chamber: string;
  district: number;
  email: string;
  session_year: string;
  committees: string;
  predicted_vote?: string;
  confidence_level?: string;
  prediction_notes?: string;
  assignment_method?: string;
  bulk_criteria?: any;
  prediction_updated_at?: string;
  note_count?: number;
}

interface VoteSummary {
  bill: {
    id: number;
    number: string;
    title: string;
    session: string;
  };
  voteCounts: Array<{
    predicted_vote: string;
    vote_count: number;
    high_confidence_count: number;
    medium_confidence_count: number;
    low_confidence_count: number;
    republican_count: number;
    democrat_count: number;
    other_party_count: number;
    house_count: number;
    senate_count: number;
  }>;
}

interface VotePredictionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  permissions: WorkspacePermissions;
  billId: string;
  billNumber: string;
  billTitle?: string;
}

/**
 * Vote predictions for a single tracked bill.
 *
 * Reached from the vote-predictions action on a tracked bill (table row or card) in the
 * Bill Tracking tab. Vote predictions is deliberately not a workspace tab of its own, so
 * the bill is always fixed by the caller and there is no bill picker here.
 *
 * Portaled to document.body so it escapes the AG Grid stacking context of the row it opens from.
 */
const VotePredictionsModal: React.FC<VotePredictionsModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  permissions,
  billId,
  billNumber,
  billTitle
}) => {
  const [legislators, setLegislators] = useState<Legislator[]>([]);
  // One batched district-map lookup for the list instead of one per row (#1179).
  useEffect(() => {
    if (legislators.length > 0) void prefetchHostedDistrictMapUrls(legislators);
  }, [legislators]);
  const [voteSummary, setVoteSummary] = useState<VoteSummary | null>(null);
  const [legislatorsLoading, setLegislatorsLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    chamber: 'All',
    committee: 'All committees',
    party: 'All parties',
    confidence: 'All levels'
  });
  const [filterOptions, setFilterOptions] = useState<{
    chambers: string[];
    parties: string[];
    committees: Array<{ name: string; chamber: string; displayName: string }>;
  }>({
    chambers: [],
    parties: [],
    committees: []
  });

  // Fetch legislators for this bill.
  // `silent` refetches in the background, leaving the current table on screen - without it
  // the spinner replaces the table and the modal visibly flashes.
  const fetchLegislators = useCallback(async (opts?: { silent?: boolean }) => {
    if (!billId) return;

    if (!opts?.silent) setLegislatorsLoading(true);
    try {
      const response = await api.get(`/workspace-vote-predictions/${workspaceId}/legislators-for-bill/${billId}`);
      setLegislators(response.data.data);
    } catch (error) {
      console.error('Error fetching legislators:', error);
      toast.error('Failed to load legislators for this bill');
    } finally {
      if (!opts?.silent) setLegislatorsLoading(false);
    }
  }, [workspaceId, billId]);

  // Fetch vote summary for this bill
  const fetchVoteSummary = useCallback(async () => {
    if (!billId) return;

    setSummaryLoading(true);
    try {
      const response = await api.get(`/workspace-vote-predictions/${workspaceId}/summary/${billId}`);
      setVoteSummary(response.data.data);
    } catch (error) {
      console.error('Error fetching vote summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  }, [workspaceId, billId]);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await api.get('/legislators/filter-options');
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, []);

  // Load data whenever the modal is opened for a bill
  useEffect(() => {
    if (!isOpen) return;

    // Reset transient UI state for the newly opened bill
    setSearchQuery('');
    setFilters({
      chamber: 'All',
      committee: 'All committees',
      party: 'All parties',
      confidence: 'All levels'
    });
    setShowBulkModal(false);

    fetchLegislators();
    fetchVoteSummary();
    fetchFilterOptions();
  }, [isOpen, fetchLegislators, fetchVoteSummary, fetchFilterOptions]);

  // Close on Escape - the innermost modal first, so Escape in Bulk Assign does not
  // close this one out from under it
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showBulkModal) setShowBulkModal(false);
      else onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, showBulkModal]);

  // Handle individual vote assignment
  const handleVoteAssignment = async (
    legislatorId: number,
    vote: string,
    confidence?: string
  ) => {
    const legislator = legislators.find(l => l.id === legislatorId);
    const finalConfidence = confidence || legislator?.confidence_level || 'medium';

    try {
      await api.post('/workspace-vote-predictions', {
        workspaceId,
        legislatorId,
        billId: parseInt(billId),
        predictedVote: vote,
        confidenceLevel: finalConfidence
      });

      // Patch the one row that changed rather than refetching the table, then refresh
      // the summary - the counts are the only other thing this affects
      setLegislators(prev => prev.map(l => (
        l.id === legislatorId
          ? { ...l, predicted_vote: vote, confidence_level: finalConfidence }
          : l
      )));
      await fetchVoteSummary();
    } catch (error) {
      console.error('Error assigning vote:', error);
      toast.error('Failed to assign vote prediction');
    }
  };

  // Handle unassigning/clearing a vote prediction
  const handleVoteUnassign = async (legislatorId: number) => {
    try {
      await api.delete(
        `/workspace-vote-predictions/${workspaceId}/bill/${billId}/legislator/${legislatorId}`
      );

      toast.success('Vote prediction cleared');

      setLegislators(prev => prev.map(l => (
        l.id === legislatorId
          ? { ...l, predicted_vote: undefined, confidence_level: undefined }
          : l
      )));
      await fetchVoteSummary();
    } catch (error) {
      console.error('Error clearing vote prediction:', error);
      toast.error('Failed to clear vote prediction');
    }
  };

  // Download CSV export
  const downloadCSV = async () => {
    try {
      const response = await api.get(
        `/workspace-vote-predictions/${workspaceId}/export/${billId}/csv`,
        { responseType: 'blob' }
      );

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'vote-predictions.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to export vote predictions');
    }
  };

  // Handle filter change
  const handleFilterChange = (filterType: keyof typeof filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      chamber: 'All',
      committee: 'All committees',
      party: 'All parties',
      confidence: 'All levels'
    });
    setSearchQuery('');
  };

  // Filter legislators based on search and filters
  const filteredLegislators = legislators.filter(legislator => {
    // Search filter
    const matchesSearch = searchQuery === '' ||
      legislator.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      legislator.party_affiliation.toLowerCase().includes(searchQuery.toLowerCase()) ||
      legislator.chamber.toLowerCase().includes(searchQuery.toLowerCase());

    // Chamber filter
    const matchesChamber = filters.chamber === 'All' || legislator.chamber === filters.chamber;

    // Party filter
    const matchesParty = filters.party === 'All parties' || matchesPartyFilter(legislator.party_affiliation, filters.party);

    // Committee filter
    const matchesCommittee = filters.committee === 'All committees' ||
      (legislator.committees && legislator.committees.toLowerCase().includes(filters.committee.toLowerCase()));

    // Confidence filter
    const matchesConfidence = filters.confidence === 'All levels' ||
      (legislator.confidence_level || 'medium') === filters.confidence;

    return matchesSearch && matchesChamber && matchesParty && matchesCommittee && matchesConfidence;
  });

  // Map a predicted vote to the StatCard variant carrying its meaning
  const getVoteVariant = (vote: string): 'success' | 'danger' | 'warning' | 'default' => {
    switch (vote) {
      case 'yes': return 'success';
      case 'no': return 'danger';
      case 'abstain': return 'warning';
      default: return 'default';
    }
  };

  // Get vote icon
  const getVoteIcon = (vote: string) => {
    switch (vote) {
      case 'yes': return <CheckCircle className="w-4 h-4 text-success-600" />;
      case 'no': return <XCircle className="w-4 h-4 text-error-600" />;
      case 'abstain': return <Minus className="w-4 h-4 text-warning-600" />;
      case 'absent': return <UserX className="w-4 h-4 text-text-tertiary" />;
      default: return null;
    }
  };

  if (!isOpen) return null;

  const canView = !!permissions?.canView;

  const body = !canView ? (
    <div className="text-center py-12">
      <Vote className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
      <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
        Access Restricted
      </h3>
      <p className="text-text-secondary dark:text-text-dark-secondary">
        You don&apos;t have permission to view vote predictions.
      </p>
    </div>
  ) : (
    <div className="space-y-6">
      {/* Vote Summary */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-text-primary dark:text-text-dark-primary flex items-center">
            <BarChart3 className="w-5 h-5 mr-2" />
            Vote Summary
          </h4>
          {summaryLoading && <Loader2 className="w-4 h-4 animate-spin text-text-tertiary" />}
        </div>

        {voteSummary && voteSummary.voteCounts.length > 0 ? (
          <StatCardRow>
            {voteSummary.voteCounts.map((count) => (
              <StatCard
                key={count.predicted_vote}
                label={count.predicted_vote.charAt(0).toUpperCase() + count.predicted_vote.slice(1)}
                value={count.vote_count}
                variant={getVoteVariant(count.predicted_vote)}
                sublabel={`H: ${count.high_confidence_count} | M: ${count.medium_confidence_count} | L: ${count.low_confidence_count}`}
              />
            ))}
          </StatCardRow>
        ) : (
          !summaryLoading && (
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              No vote predictions recorded for this bill yet.
            </p>
          )
        )}
      </div>

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          {/* Search */}
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search legislators by name..."
            size="sm"
            className="w-full"
          />

          {/* Chamber Filter */}
          <select
            value={filters.chamber}
            onChange={(e) => handleFilterChange('chamber', e.target.value)}
            className="input"
          >
            <option value="All">All Chambers</option>
            {filterOptions.chambers.map(chamber => (
              <option key={chamber} value={chamber}>{chamber}</option>
            ))}
          </select>

          {/* Party Filter */}
          <select
            value={filters.party}
            onChange={(e) => handleFilterChange('party', e.target.value)}
            className="input"
          >
            <option value="All parties">All Parties</option>
            {filterOptions.parties.map(party => (
              <option key={party} value={party}>{party}</option>
            ))}
          </select>

          {/* Committee Filter */}
          <select
            value={filters.committee}
            onChange={(e) => handleFilterChange('committee', e.target.value)}
            className="input"
          >
            <option value="All committees">All Committees</option>
            {filterOptions.committees.map(committee => (
              <option key={committee.name} value={committee.name}>
                {committee.displayName}
              </option>
            ))}
          </select>

          {/* Confidence Filter */}
          <select
            value={filters.confidence}
            onChange={(e) => handleFilterChange('confidence', e.target.value)}
            className="input"
          >
            <option value="All levels">All Confidence Levels</option>
            <option value="high">High Confidence</option>
            <option value="medium">Medium Confidence</option>
            <option value="low">Low Confidence</option>
          </select>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Showing {filteredLegislators.length} of {legislators.length} legislators
          </p>
          <FilterActions onClear={clearFilters} />
        </div>
      </div>

      {/* Legislators Table */}
      <div className="card p-0 overflow-hidden">
        {legislatorsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-text-tertiary" />
          </div>
        ) : filteredLegislators.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
            <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
              No legislators found
            </h3>
            <p className="text-text-secondary dark:text-text-dark-secondary">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        ) : (
          <StandardTable horizontalScroll>
          {/*
              StandardTable, not a hand-rolled <table>. The head was
              `bg-background-tertiary` with grey uppercase labels; the app standard
              is `thead.table-header` — primary-800 fill, white bold uppercase — which
              StandardTableHeader applies. Every other data table in the app already
              uses it, so this one read as a different product.
            */}
            <StandardTableHeader>
              <tr>
                <StandardTableHeaderCell>Legislator</StandardTableHeaderCell>
                <StandardTableHeaderCell>Party</StandardTableHeaderCell>
                <StandardTableHeaderCell>Chamber</StandardTableHeaderCell>
                <StandardTableHeaderCell>Current vote</StandardTableHeaderCell>
                <StandardTableHeaderCell>Confidence</StandardTableHeaderCell>
                <StandardTableHeaderCell>Assign vote</StandardTableHeaderCell>
                <StandardTableHeaderCell>Notes</StandardTableHeaderCell>
              </tr>
            </StandardTableHeader>
            <StandardTableBody>
              {filteredLegislators.map((legislator) => (
                <StandardTableRow key={legislator.id}>
                    {/* Legislator Name */}
                    <StandardTableCell>
                      <div className="flex items-center">
                        <div>
                          {permissions.role === 'guest' ? (
                            <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {formatLegislatorNameFromObject(legislator)}
                            </div>
                          ) : (
                            <Link
                              to={`/legislators/${legislator.id}`}
                              className={`text-sm ${getLegislatorLinkClasses(legislator.party_affiliation, { neutral: true })}`}
                            >
                              {formatLegislatorNameFromObject(legislator)}
                              <ExternalLink className="w-3 h-3 inline ml-1 text-text-tertiary" />
                            </Link>
                          )}
                          <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                            <DistrictMapLink
                              chamber={legislator.chamber}
                              district={legislator.district}
                              className="text-sm text-text-tertiary dark:text-text-dark-tertiary"
                            >
                              {formatDistrict(null, String(legislator.district ?? ''), legislator.chamber)}
                            </DistrictMapLink>
                          </div>
                        </div>
                      </div>
                    </StandardTableCell>

                    {/* Party */}
                    <StandardTableCell>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPartyBadgeColor(legislator.party_affiliation)}`}>
                        {formatPartyDisplay(legislator.party_affiliation)}
                      </span>
                    </StandardTableCell>

                    {/* Chamber */}
                    <StandardTableCell>
                      {legislator.chamber}
                    </StandardTableCell>

                    {/* Current Vote */}
                    <StandardTableCell>
                      {legislator.predicted_vote ? (
                        <div className="flex items-center">
                          {getVoteIcon(legislator.predicted_vote)}
                          <span className="ml-2 text-sm font-medium capitalize">
                            {legislator.predicted_vote}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Not set</span>
                      )}
                    </StandardTableCell>

                    {/* Confidence */}
                    <StandardTableCell>
                      {permissions?.canEdit ? (
                        <select
                          value={legislator.confidence_level || 'medium'}
                          onChange={(e) => handleVoteAssignment(
                            legislator.id,
                            legislator.predicted_vote || 'abstain',
                            e.target.value
                          )}
                          className="text-sm border border-border-light dark:border-border-dark rounded px-2 py-1 bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                      ) : (
                        <span className="text-sm text-text-secondary dark:text-text-dark-secondary capitalize">
                          {legislator.confidence_level || 'medium'}
                        </span>
                      )}
                    </StandardTableCell>

                    {/* Assign Vote Buttons - only for users with edit permissions */}
                    <StandardTableCell>
                      {permissions?.canEdit ? (
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleVoteAssignment(legislator.id, 'yes')}
                            className={`w-8 h-8 rounded flex items-center justify-center text-white font-bold text-sm transition-colors ${
                              legislator.predicted_vote === 'yes'
                                ? 'bg-success-600 hover:bg-success-700'
                                : 'bg-background-quaternary hover:bg-success-500'
                            }`}
                            title="Predict Yes"
                          >
                            Y
                          </button>
                          <button
                            onClick={() => handleVoteAssignment(legislator.id, 'no')}
                            className={`w-8 h-8 rounded flex items-center justify-center text-white font-bold text-sm transition-colors ${
                              legislator.predicted_vote === 'no'
                                ? 'bg-error-600 hover:bg-error-700'
                                : 'bg-background-quaternary hover:bg-error-500'
                            }`}
                            title="Predict No"
                          >
                            N
                          </button>
                          <button
                            onClick={() => handleVoteAssignment(legislator.id, 'abstain')}
                            className={`w-8 h-8 rounded flex items-center justify-center text-white font-bold text-sm transition-colors ${
                              legislator.predicted_vote === 'abstain'
                                ? 'bg-warning-600 hover:bg-warning-700'
                                : 'bg-background-quaternary hover:bg-warning-500'
                            }`}
                            title="Predict Abstain"
                          >
                            A
                          </button>
                          <button
                            onClick={() => handleVoteAssignment(legislator.id, 'absent')}
                            className={`w-8 h-8 rounded flex items-center justify-center text-white font-bold text-sm transition-colors ${
                              legislator.predicted_vote === 'absent'
                                ? 'bg-text-tertiary hover:bg-text-secondary'
                                : 'bg-background-quaternary hover:bg-text-tertiary'
                            }`}
                            title="Predict Absent"
                          >
                            X
                          </button>
                          {/* Clear/Unassign button - only visible when vote is assigned */}
                          {legislator.predicted_vote && (
                            <button
                              onClick={() => handleVoteUnassign(legislator.id)}
                              className="w-8 h-8 rounded flex items-center justify-center text-text-tertiary hover:text-error-600 hover:bg-error-100 dark:hover:bg-error-900/20 transition-colors ml-1"
                              title="Clear prediction"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                          View Only
                        </div>
                      )}
                    </StandardTableCell>

                    {/* Notes - Using Notes V2 - tied to legislator+bill combo */}
                    <StandardTableCell>
                      <NotesCell
                        legislatorId={legislator.id.toString()}
                        legislatorName={legislator.full_name}
                        billId={billId}
                        billNumber={billNumber}
                        workspaceId={workspaceId}
                        noteCount={legislator.note_count || 0}
                      />
                    </StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>
        )}
      </div>
    </div>
  );

  return (
    <>
    <Modal
      isOpen={true}
      title="Vote predictions"
      description={`${billNumber}${billTitle ? ` - ${billTitle}` : ''}`}
      onClose={onClose}
      size="xl"
      className="sm:max-w-7xl"
      /* Bulk assign and export act on the whole table, so they stay pinned
         above it rather than scrolling away with the rows. */
      subheader={
        canView && permissions?.canEdit ? (
          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" icon={Users} onClick={() => setShowBulkModal(true)}>
              Bulk assign
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={Download}
              onClick={downloadCSV}
            >
              Export CSV
            </Button>
            <span className="ml-auto hidden md:inline-flex">
              <HelpTrigger contentKey="workspace-vote-predictions" size="md" />
            </span>
          </div>
        ) : undefined
      }
    >
      {body}
    </Modal>

    {/* Still a sibling rather than a child: each <Modal> portals to <body> and
        ref-counts the scroll lock, so a nested dialog stacks correctly on its
        own — but keeping it outside also keeps its state out of this panel's
        scroll container. */}
    {showBulkModal && (
      <BulkVotePredictionModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        workspaceId={workspaceId}
        billId={billId}
        onSuccess={async () => {
          toast.success('Bulk vote predictions assigned successfully');
          // Many rows change here, so refetch - silently, to keep the table on screen
          await Promise.all([
            fetchLegislators({ silent: true }),
            fetchVoteSummary()
          ]);
        }}
      />
    )}
    </>
  );
};

export default VotePredictionsModal;
